import { FC, ReactNode, isValidElement } from 'react';
import { Link } from 'react-router-dom';

import HomeIcon from '@mui/icons-material/Home';
import {
  Breadcrumbs,
  Button,
  ButtonProps,
  Link as MUILink,
  Tooltip,
  TooltipProps,
  Typography,
} from '@mui/material';
import { Stack } from '@mui/system';

import { AppPath } from '@/shared/constants/path';

const iconStyle = {
  width: 16,
  height: 16,
  color: 'text.secondary',
};

const linkStyles = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  textDecoration: 'none',
  color: 'text.secondary',
};

type TooltipPropsCustom = Omit<TooltipProps, 'children'>;

type Action = {
  label: string;
  icon: ReactNode;
  button: ButtonProps;
};

type PageTitleProps = {
  title: string | { text: string; tooltip: TooltipPropsCustom };
  breadcrumbs: {
    label: string;
    icon?: ReactNode;
    to?: string;
    tooltip?: TooltipPropsCustom;
  }[];
  actions?: (Action | null)[] | ReactNode;
  customColumn?: ReactNode;
};

const PageTitle: FC<PageTitleProps> = ({
  title,
  breadcrumbs,
  actions,
  customColumn,
}) => {
  return (
    <Stack
      direction='row'
      sx={{
        alignItems: 'center',
        justifyContent: 'space-between',
        mx: -3,
        mb: 2,
        px: 3,
        pt: 2,
        position: 'sticky',
        top: 0,
        borderBottom: '1px solid',
        borderColor: 'grey.300',
        backgroundColor: 'grey.100',
        zIndex: 'appBar',
      }}
    >
      <Stack spacing={1} sx={{ mb: 2 }}>
        {typeof title === 'object' ? (
          <Tooltip {...title.tooltip}>
            <Typography variant='h5' sx={{ width: 'max-content' }}>
              {title.text}
            </Typography>
          </Tooltip>
        ) : (
          <Typography variant='h5'>{title}</Typography>
        )}

        <Breadcrumbs>
          <MUILink component={Link} to={AppPath.Root} sx={{ display: 'flex' }}>
            <HomeIcon sx={iconStyle} />
          </MUILink>

          {breadcrumbs.map(({ label, icon, to, tooltip }, index) => {
            if (!to || index === breadcrumbs.length - 1) {
              return tooltip ? (
                <Tooltip {...tooltip} key={label}>
                  <Typography sx={linkStyles}>
                    {icon}
                    {label}
                  </Typography>
                </Tooltip>
              ) : (
                <Typography key={label} sx={linkStyles}>
                  {icon}
                  {label}
                </Typography>
              );
            }

            return tooltip ? (
              <Tooltip {...tooltip} key={label}>
                <MUILink component={Link} to={to} sx={linkStyles}>
                  {icon}
                  {label}
                </MUILink>
              </Tooltip>
            ) : (
              <MUILink key={label} component={Link} to={to} sx={linkStyles}>
                {icon}
                {label}
              </MUILink>
            );
          })}
        </Breadcrumbs>
      </Stack>

      {customColumn}

      {isValidElement(actions) ? (
        actions
      ) : (
        <Stack direction='row' spacing={1}>
          {(actions as (Action | null)[])?.map((action) =>
            action ? (
              <Button key={action.label} {...action.button}>
                {action.icon}
                {action.label}
              </Button>
            ) : null,
          )}
        </Stack>
      )}
    </Stack>
  );
};

export default PageTitle;
